import { Modal } from "antd";
import { twMerge } from "tailwind-merge";
import { Trans, useTranslation } from "react-i18next";
import { IconModalCloseGrey } from "@icons/index";
import { useAppSelector } from "@hooks/redux";
import { CDN_UPLOAD_URL } from "@config";
import { useEffect, useMemo, useRef, useState } from "react";
import commonservice from "@apis/commonservice";
import report, { reportImmediately } from "@report";
import message from "@utils/message";
import Loading from "@src/ui/Loading";
import { useHeaderTopLabel } from "@hooks/useHeaderTopLabel";
import IconSuccess from "./success.svg?react";
import { useNavigate } from "react-router-dom";
import CommonButton from "@components/CommonButton";

interface WxMemberModalPropsType {
  open: boolean;
  onClose: (bindSuccess: boolean) => void;
  modalInfo: {
    bgUrl: string;
    days: string;
    membersText: string;
  };
}
const WxMemberModal = ({ open, onClose, modalInfo }: WxMemberModalPropsType) => {
  const { t } = useTranslation();
  const { language, userInfo } = useAppSelector((state) => state.globalReducer);
  const isMountedRef = useRef(true); // 跟踪组件是否已挂载

  const [wxCode, setWxCode] = useState<string>("");
  const [sceneStr, setSceneStr] = useState<string>("");
  const [polling, setPolling] = useState<boolean>(false);
  const [timeoutCount, setTimeoutCount] = useState<number>(0);
  const MAX_TIMEOUT = 135; // 最多轮询135次，约4分半钟 (135 * 2秒 = 270秒 = 4.5分钟)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getQrcode = async () => {
    if (!isMountedRef.current) return;
    try {
      // 重置状态
      setTimeoutCount(0);
      setPolling(false);

      // 清除可能存在的timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const { sceneStr: newSceneStr, qrCodeUrl } = await commonservice.getWxQrUrl();

      if (!isMountedRef.current) return;

      setWxCode(qrCodeUrl);
      setSceneStr(newSceneStr);

      report.event("wx_code", {
        kalo_value: "show_qr_code",
        kalo_type: "bind_wechat",
      });

      if (newSceneStr) {
        setPolling(true); // 这会触发useEffect开始轮询
      }
    } catch (error) {
      console.error("获取二维码失败:", error);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    getQrcode();
    return () => {
      // 组件卸载时清理
      isMountedRef.current = false;
      setPolling(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const { handleRemoveWxAd } = useHeaderTopLabel();

  useEffect(() => {
    // 当polling状态或sceneStr变化时，开始或停止轮询
    if (polling && sceneStr && timeoutCount < MAX_TIMEOUT) {
      // 开始轮询
      pollLoginStatus();
    } else {
      // 停止轮询
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    return () => {
      // 清理
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [polling, sceneStr]);

  // 轮询登录状态
  const pollLoginStatus = async () => {
    if (!isMountedRef.current || !polling || timeoutCount >= MAX_TIMEOUT) {
      return;
    }

    try {
      const result = await commonservice.fetchBindWechat({ sceneStr });

      if (!isMountedRef.current) return; // 如果组件已卸载，不继续处理

      const { success, exist, used } = result;

      if (success) {
        if (!exist && !used) {
          report.event("wx_bind", {
            kalo_value: "wxcode_success",
          });
          await reportImmediately();

          // window.location.href = window.location.href;
          handleRemoveWxAd();
          onClose(true);
        }
        //   代表当前账号已经绑定微信
        if (exist && !used) {
          // window.location.href = window.location.href;
          handleRemoveWxAd();
          onClose(true);
        }
        //   代表当前扫码的微信已经绑定了别的账号
        if (!exist && used) {
          message.warning(t("The wechat is linked to another account. You can try another wechat."));
          onClose(false);
        }
      } else {
        // 修改：不更新timeoutCount，而是直接设置下一次轮询
        if (isMountedRef.current) {
          // 检查是否达到最大轮询次数
          const newCount = timeoutCount + 1;
          if (newCount >= MAX_TIMEOUT) {
            // 达到最大轮询次数，刷新二维码
            setPolling(false);
            setTimeout(() => {
              if (isMountedRef.current) {
                getQrcode();
              }
            }, 100);
          } else {
            // 更新计数器但不触发重新渲染
            setTimeoutCount((prev) => prev + 1);
            // 设置下一次轮询
            timeoutRef.current = setTimeout(pollLoginStatus, 2000);
          }
        }
      }
    } catch (error) {
      console.error("轮询出错:", error);

      if (!isMountedRef.current) return;

      // 修改：不更新timeoutCount，而是直接设置下一次轮询
      // 检查是否达到最大轮询次数
      const newCount = timeoutCount + 1;
      if (newCount >= MAX_TIMEOUT) {
        // 达到最大轮询次数，刷新二维码
        setPolling(false);
        setTimeout(() => {
          if (isMountedRef.current) {
            getQrcode();
          }
        }, 100);
      } else {
        // 更新计数器但不触发重新渲染
        setTimeoutCount((prev) => prev + 1);
        // 设置下一次轮询
        timeoutRef.current = setTimeout(pollLoginStatus, 2000);
      }
    }
  };

  const ChineseInfoBox = useMemo(() => {
    return (
      <div className="flex justify-between">
        {!userInfo?.subAccount ? (
          <div className="text-[24px] ml-[37px] mt-[155px] text-white flex-1line-clamp-1">
            领
            <span className=" text-[#F9E8AA] font-semibold ">
              {modalInfo.days}天<Trans>{modalInfo.membersText}</Trans>会员
            </span>
            !
          </div>
        ) : (
          <div className="w-[345px] flex-none"></div>
        )}
        <div className="flex w-[220px] h-[220px] justify-center items-center mt-[40px] mr-[50px] shrink-0">
          {wxCode && (
            <>
              <img src={wxCode} className="rounded-[8px]" key="qrcode" />
              <img
                src={`${CDN_UPLOAD_URL}/upload/9ffd2853.png`}
                className="absolute w-[32px] h-[32px]"
                alt=""
                key="overlay"
              />
            </>
          )}
          {!wxCode && (
            <div className="w-[181px] h-[181px] flex justify-center items-center">
              <Loading />
            </div>
          )}
        </div>
      </div>
    );
  }, [wxCode, modalInfo.days, modalInfo.membersText, userInfo]);

  const EnglishInfoBox = useMemo(() => {
    return (
      <div className="flex justify-between">
        {!userInfo?.subAccount ? (
          <div className="text-[24px] ml-[32px] mt-[135px] leading-[35px] text-white ">
            to receive <span className=" text-[#F9E8AA] font-semibold ">{modalInfo.days}days</span> of{" "}
            <span className=" text-[#F9E8AA] font-semibold ">{modalInfo.membersText} membership</span>.
          </div>
        ) : (
          <div className="w-[345px] flex-none"></div>
        )}
        <div className="flex w-[181px] h-[181px] justify-center items-center mt-[100px] mr-[30px] shrink-0">
          {wxCode && (
            <>
              <img src={wxCode} className="rounded-[6px]" key="qrcode" />
              <img
                src={`${CDN_UPLOAD_URL}/upload/9ffd2853.png`}
                className="absolute w-[32px] h-[32px]"
                alt=""
                key="overlay"
              />
            </>
          )}
          {!wxCode && (
            <div className="w-[181px] h-[181px] flex justify-center items-center">
              <Loading />
            </div>
          )}
        </div>
      </div>
    );
  }, [wxCode, modalInfo.days, modalInfo.membersText, userInfo]);
  //   if (!open) return null;
  return (
    <Modal
      open={open}
      onCancel={() => {
        onClose(false);
      }}
      destroyOnHidden={true}
      footer={null}
      className="h-[320px]"
      width={600}
      centered={true}
      afterOpenChange={() => {}}
      closeIcon={<IconModalCloseGrey className="w-5 h-5" />}
    >
      <div
        style={{ backgroundImage: `url(${modalInfo.bgUrl})` }}
        className={twMerge("bg-center bg-cover bg-no-repeat shrink-0 h-[320px] relative")}
      >
        {language === "zh-CN" ? ChineseInfoBox : EnglishInfoBox}
      </div>
    </Modal>
  );
};

export default WxMemberModal;

export const WxVerfiedModal = ({
  verfiedModal,
  setVerfiedModal,
}: {
  verfiedModal: boolean;
  setVerfiedModal: (value: boolean) => void;
}) => {
  const { language } = useAppSelector((state) => state.globalReducer);
  const navigate = useNavigate();
  return (
    <Modal
      className=""
      open={verfiedModal}
      footer={false}
      onCancel={() => {
        setVerfiedModal(false);
      }}
    >
      <div className="p-[16px]">
        <div className="flex flex-row gap-[10px]">
          <IconSuccess></IconSuccess>
          <div className="text-[16px] font-medium">
            <Trans>Verified successfully</Trans>
          </div>
        </div>

        <div className="mt-[8px]">
          {language === "zh-CN" && (
            <div className="flex flex-col gap-[4px] ">
              <div>会员已到账</div>
              <div className="flex flex-row items-center gap-[4px]">
                您可点击
                <div className="text-[16px] text-kds-accent flex flex-row items-center gap-[4px]">
                  <div>右上角头像</div>-<div>会员管理中查看</div>
                </div>
              </div>
            </div>
          )}
          {language !== "zh-CN" && (
            <div className="flex flex-col gap-[4px] ">
              <div>You membership is now active.</div>
              <div className="">
                <Trans>You can view it in by clicking the avatar in the</Trans>
                <div className="text-[16px] text-kds-accent flex flex-row items-center gap-[4px]">
                  <div>
                    <Trans>top right corner</Trans>
                  </div>
                  {` > `}
                  <div>Membership Management</div>
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-row items-center gap-[8px] justify-end mt-[12px]">
            <CommonButton
              className="w-[134px] h-[34px]"
              type="primary"
              onClick={() => {
                navigate("/me?tab=membership");
                setVerfiedModal(false);
              }}
            >
              {language === "zh-CN" ? "立即前往" : "Go Now"}
            </CommonButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};
